import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError, isTurnstileError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AuthShell } from "../components/AuthShell";
import { RATE_LIMIT_ERROR_MESSAGE, Turnstile, TURNSTILE_ERROR_MESSAGE, turnstileEnabled } from "../components/Turnstile";
import { Button, Field, Input, ErrorText } from "../components/ui";

/** Map the accept endpoint's error envelope to a message the invitee can act on. */
function acceptErrorMessage(err: unknown): string {
  if (isTurnstileError(err)) return TURNSTILE_ERROR_MESSAGE;
  if (err instanceof ApiError) {
    if (err.status === 429) return RATE_LIMIT_ERROR_MESSAGE;
    if (err.code === "CONFLICT") return "Ya existe una cuenta con este email. Inicia sesión con tu contraseña.";
    if (err.code === "VALIDATION" && /expired/i.test(err.message)) return "La invitación ha caducado. Pide que te envíen una nueva.";
    if (err.code === "NOT_FOUND") return "La invitación no es válida o ya se ha usado. Pide que te envíen una nueva.";
  }
  return "No se pudo crear la cuenta. Inténtalo de nuevo en unos minutos.";
}

export function AcceptInvitation() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/invitations/accept", { token, name, password, turnstileToken });
      await refresh();
      void navigate("/");
    } catch (err) {
      setError(acceptErrorMessage(err));
      // Turnstile tokens are single-use: get a fresh challenge for the next try.
      setTurnstileToken("");
      setAttempt((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title="Invitación no válida">
        <p className="text-sm text-muted">Falta el token de invitación en el enlace.</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Crea tu cuenta" subtitle="Has sido invitado a Eurocar Inspect">
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4">
        {error && <ErrorText>{error}</ErrorText>}
        <Field label="Nombre">
          <Input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
        </Field>
        <Field label="Contraseña" hint="Mínimo 8 caracteres.">
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
        </Field>
        <Turnstile key={attempt} onToken={setTurnstileToken} />
        <Button type="submit" disabled={busy || (turnstileEnabled && !turnstileToken)}>{busy ? "Creando…" : "Crear cuenta"}</Button>
      </form>
    </AuthShell>
  );
}
